import React, { useEffect, useState } from 'react';
import { ButtonV2, CopyButton } from '@asigna/kit';
import classNames from 'classnames';
import { NonceInput, SetTxFee, TransactionCommonDialog } from 'components';
import { usePermissionWarning } from 'hooks';
import useAddress from 'hooks/use-address';
import useNetwork from 'hooks/use-network';
import useSafe from 'hooks/use-safe';
import { useSafeCalls } from 'hooks/use-safe-call';
import { QRCodeSVG } from 'qrcode.react';
import { ISubscriptionOrder } from 'types';
import { Tab } from 'ui-kit';
import { shortenAddress } from 'utils/helper';
import { generateUnsignedStxTransfer } from 'utils/transaction-create';

import { useCompleteBRCInscriptionStyles } from './style';

interface TransactionCommonProps {
  open: boolean;
  onClose: () => void;
  order: ISubscriptionOrder;
  onDone: (isMultiTx: boolean, isAddMultiTx: boolean, txName: string) => void;
}

const tabs = ['Pay with address', 'Pay with Vault'] as const;

type Tabs = (typeof tabs)[number];

export const CompleteOrder: React.FC<TransactionCommonProps> = ({ open, onClose, order }) => {
  const [selectTable, setSelectTable] = useState<Tabs>('Pay with address');
  const classes = useCompleteBRCInscriptionStyles();
  const [step, setStep] = useState<1 | 2>(1);
  const [generatedTxHex, setGeneratedTxHex] = useState('');

  const { safe } = useSafe();
  const [nonce, setNonce] = useState<number>();
  const [, setHasNonceError] = useState(false);
  const { nativeSendSTX } = useSafeCalls();

  const disabled = step === 2;

  const permissionCreateWarning = usePermissionWarning('PROPOSE');

  const handleSend = async (fee: number) => {
    if (!nonce) {
      return;
    }
    await nativeSendSTX(
      nonce,
      order.receiverAddress,
      Number(order.stacksTotalPrice),
      fee,
      order._id,
      'Asigna Subscription'
    );

    onClose();
  };

  const { stxBalance } = useSafe();

  const Title = () => {
    return (
      <h2 className={classNames(classes.modalTitle, classes.cursorUnset)}>
        <span className={classes.titleContent}>
          {step === 1 ? 'Asigna Pro subscription' : 'Fee'}
        </span>
      </h2>
    );
  };

  const ContinueButton = () => {
    const rightButtonText = step === 1 ? 'Next' : 'Confirm';
    if (selectTable === 'Pay with address' || step === 2) return <></>;

    return (
      <div className="grid w-full gap-8">
        <ButtonV2
          onClick={() => setStep(2)}
          message={rightButtonText}
          className="w-full"
          color="primary"
          disabled={disabled || (step === 1 && Boolean(permissionCreateWarning))}
          disableHint={step === 1 ? permissionCreateWarning : undefined}
          hintPosition="top"
        />
      </div>
    );
  };
  const walletAccount = useAddress();
  const [, stacksNetwork, ,] = useNetwork();

  useEffect(() => {
    if (!nonce || !walletAccount) {
      return;
    }
    (async function () {
      const tx = await generateUnsignedStxTransfer(
        nonce,
        walletAccount,
        Number(0),
        stacksNetwork,
        safe.owners.map((owner) => owner.publicKey),
        safe.threshold,
        safe.address,
        undefined,
        ''
      );
      setGeneratedTxHex(tx);
    })();
  }, [nonce, walletAccount, stacksNetwork, safe]);

  return (
    <TransactionCommonDialog open={open} title="" button={<ContinueButton />} onClose={onClose}>
      <Title />
      <div className="flex flex-col w-full mt-24">
        {step === 1 && (
          <>
            <div className="flex items-center gap-8 mb-24">
              {tabs.map((tab) => (
                <Tab id={tab} key={tab} onClick={setSelectTable} isActive={tab === selectTable} />
              ))}
            </div>

            {selectTable === 'Pay with Vault' && step === 1 && (
              <div className="flex flex-col w-full py-8 px-24 rounded-[8px] bg-white-3 mb-32">
                {safe.address && (
                  <>
                    <div className="py-8 w-full">
                      <NonceInput
                        value={nonce}
                        onChange={(newNonce) => setNonce(newNonce)}
                        onError={(hasError) => setHasNonceError(hasError)}
                        fontClassName="text-m14 text-regular"
                      />
                    </div>
                    <div className="w-full h-[.5px] bg-white-10" />
                  </>
                )}
                <div className="flex items-center py-8 w-full justify-between">
                  <span className="text-m14 text-regular">Amount</span>
                  <div className={classes.copyCodeTotal}>
                    <div className="flex items-center gap-4">
                      <span className="text-m12 text-regular">${order.usdPrice}</span>
                      <div className="w-4 h-4 rounded-[50%] bg-regular" />
                      <p className="text-m14 text-white">
                        {Number(order.stacksTotalPrice) / Math.pow(10, 6)} STX
                      </p>
                    </div>
                    <CopyButton
                      copy={(Number(order.stacksTotalPrice) / Math.pow(10, 6)).toString()}
                    />
                  </div>
                </div>
                <div className="w-full h-[.5px] bg-white-10" />
                <div className="flex items-center py-8 w-full justify-between">
                  <span className="text-m14 text-regular">Receiver</span>
                  <div className={classes.copyCodeTotal}>
                    <div className="flex items-center gap-4">
                      <p>{shortenAddress(order.receiverAddress)}</p>
                    </div>
                    <CopyButton copy={order.receiverAddress} />
                  </div>
                </div>
                <div className="w-full h-[.5px] bg-white-10" />
                <div className="flex items-center py-8 w-full justify-between">
                  <span className="text-m14 text-regular">Available balance</span>
                  <div className={classes.copyCodeTotal}>
                    <div className="flex items-center gap-4">
                      <p className="text-m14 text-white">{stxBalance.available.toString()} STX</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <div className={classes.description}>
          {selectTable === 'Pay with address' && step === 1 && (
            <div className={classes.qrcode}>
              <div className={classes.imageWrapper}>
                <QRCodeSVG value={order.receiverAddress} size={178} />
              </div>
              <div className="flex items-center w-full justify-between mt-24">
                <span className="text-m14 text-regular">Amount</span>
                <div className={classes.copyCodeTotal}>
                  <div className="flex items-center gap-4">
                    <span className="text-m12 text-regular">${order.usdPrice}</span>
                    <div className="w-4 h-4 rounded-[50%] bg-regular" />
                    <p className="text-m14 text-white">
                      {Number(order.stacksTotalPrice) / Math.pow(10, 6)} STX
                    </p>
                  </div>
                </div>
              </div>
              <div className={classes.copyCode}>
                <p>{order.receiverAddress}</p>
                <CopyButton copy={order.receiverAddress} className="shrink-0" />
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="mt-[-32px]">
              <SetTxFee
                onBackClick={() => setStep(1)}
                onSend={handleSend}
                transactionHex={generatedTxHex}
              />
            </div>
          )}
        </div>
      </div>
    </TransactionCommonDialog>
  );
};
